import React from 'react';
import styled from 'styled-components';
import { BillingBreakdownDto, formatCurrency, formatDate } from '../../services/paymentsService';

interface ChargeBreakdownProps {
  breakdown?: BillingBreakdownDto;
}

const Container = styled.div`
  padding: 20px 0;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const GridItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color-light);

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span<{ $secondary?: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$secondary ? 'var(--text-secondary)' : 'var(--text-primary)'};
`;

const Value = styled.span<{ $highlight?: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$highlight ? '#16a34a' : 'var(--text-primary)'};
  direction: ltr;
`;

const Divider = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  background: var(--border-color);
  margin: 8px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
  margin-top: 16px;
`;

const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const TotalValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
  direction: ltr;
`;

const NotesSection = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  color: var(--text-secondary);
`;

const NotesLabel = styled.span`
  font-weight: 600;
  margin-left: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: var(--text-secondary);
  font-size: 13px;
`;

const ChargeBreakdown: React.FC<ChargeBreakdownProps> = ({ breakdown }) => {
  if (!breakdown) {
    return (
      <Container>
        <Title>פירוט החיוב</Title>
        <EmptyState>טרם הוזן מידע על חיובים</EmptyState>
      </Container>
    );
  }

  const hasMonthlyBenefits = (breakdown.monthlyBenefitAmount ?? 0) > 0 && (breakdown.numberOfMonths ?? 0) > 0;
  const hasRetroactive = (breakdown.retroactiveTotalAmount ?? 0) > 0;

  return (
    <Container>
      <Title>פירוט החיוב</Title>

      <Grid>
        {/* Committee and Commission */}
        {breakdown.committeeId && (
          <GridItem>
            <Label>מספר ועדה</Label>
            <Value>{breakdown.committeeId}</Value>
          </GridItem>
        )}
        <GridItem>
          <Label>אחוז עמלה</Label>
          <Value $highlight>{breakdown.commissionPercentage}%</Value>
        </GridItem>

        {/* Monthly Benefits Section */}
        {hasMonthlyBenefits && (
          <>
            <Divider />
            <GridItem>
              <Label $secondary>גמלה חודשית</Label>
              <Value>{formatCurrency(breakdown.monthlyBenefitAmount)}</Value>
            </GridItem>
            <GridItem>
              <Label $secondary>מספר חודשים</Label>
              <Value>{breakdown.numberOfMonths}</Value>
            </GridItem>
            <GridItem>
              <Label $secondary>סה"כ גמלאות</Label>
              <Value>{formatCurrency(breakdown.totalBenefits)}</Value>
            </GridItem>
            <GridItem>
              <Label>עמלה על גמלאות</Label>
              <Value $highlight>{formatCurrency(breakdown.commissionOnBenefits)}</Value>
            </GridItem>
          </>
        )}

        {/* Retroactive Section */}
        {hasRetroactive && (
          <>
            <Divider />
            {breakdown.retroactiveFromDate && (
              <GridItem>
                <Label $secondary>רטרואקטיב מתאריך</Label>
                <Value>{formatDate(breakdown.retroactiveFromDate)}</Value>
              </GridItem>
            )}
            <GridItem>
              <Label $secondary>סכום רטרואקטיב</Label>
              <Value>{formatCurrency(breakdown.retroactiveTotalAmount)}</Value>
            </GridItem>
            <GridItem>
              <Label>עמלה על רטרואקטיב</Label>
              <Value $highlight>{formatCurrency(breakdown.commissionOnRetroactive)}</Value>
            </GridItem>
          </>
        )}
      </Grid>

      {/* Total */}
      <TotalRow>
        <TotalLabel>סה"כ לחיוב</TotalLabel>
        <TotalValue>{formatCurrency(breakdown.totalCharge)}</TotalValue>
      </TotalRow>

      {/* Notes */}
      {breakdown.notes && (
        <NotesSection>
          <NotesLabel>הערות:</NotesLabel>
          {breakdown.notes}
        </NotesSection>
      )}
    </Container>
  );
};

export default ChargeBreakdown;
